import React from 'react';
import { Briefcase, Calendar, Edit, FileText, Mail, MapPin, MessageSquare, Phone, Plus, Smartphone, User, X } from 'lucide-react';
import { formatDate } from '../../../../lib/dateFormat';
import type { PersonType } from '../../../../lib/person-types';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { TabsContent } from '../../../ui/tabs';
import { Textarea } from '../../../ui/textarea';
import PersonCollapsibleSection from '../PersonCollapsibleSection';

const COMM_TYPE_LABELS: Record<string, string> = {
  email: 'E-Mail',
  mobil: 'Mobil',
  festnetz: 'Festnetz',
  sms: 'SMS',
  phone: 'Telefon',
};
const COMM_TYPE_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  mobil: Smartphone,
  festnetz: Phone,
  sms: MessageSquare,
  phone: Phone,
};

// Adresse für die Nur-Lese-Ansicht formatieren. Der Backend-`address`-String kann
// bei frisch angelegten Personen leer sein (persistiert wird primär die
// strukturierte Adresse) — dann aus street/PLZ/Ort einen Anzeigetext bauen,
// damit die gespeicherte Adresse nicht als "-" erscheint.
function formatStructuredAddress(addr: unknown): string {
  if (!addr || typeof addr !== 'object') return '';
  const a = addr as Record<string, unknown>;
  const formatted = String(a.formatted || '').trim();
  if (formatted) return formatted;
  const line1 = [a.street, a.houseNumber].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
  const line2 = [a.zip || a.postalCode, a.city].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ');
}

type Props = {
  person: any;
  isEditing: boolean;
  isSaving: boolean;
  formData: any;
  personTypes: PersonType[];
  getTypeIcon: (type: string) => React.ReactNode;
  getProfileTypeDescription: () => string;
  getStatusBadgeClass: (status: string) => string;
  getStatusText: (status: string) => string;
  getAgeText: (birthDate: string) => string;
  handleSelectChange: (name: string, value: string) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleCheckboxChange: (name: string, checked: boolean | 'indeterminate') => void;
  addressQuery: string;
  onAddressQueryChange: (value: string) => void;
  addressSuggestions: any[];
  onAddressSuggestionSelect: (suggestion: any) => void;
  onStructuredAddressFieldChange: (field: 'street' | 'zip' | 'city', value: string) => void;
  onCommunicationTypeChange: (index: number, value: string) => void;
  onCommunicationValueChange: (index: number, value: string) => void;
  onCommunicationOfficeAllowedChange: (index: number, allowed: boolean) => void;
  onRemoveCommunication: (index: number) => void;
  onAddCommunication: () => void;
  canEditStandorte: boolean;
  isEditingStandorte: boolean;
  setIsEditingStandorte: (value: boolean) => void;
  standorte: Array<{ id: number; name: string }>;
  personStandorteIds: number[];
  /** Standorte aus Probestunden und Kursen — nur angezeigt, nicht bearbeitbar. */
  abgeleiteteStandorteIds?: number[];
  onToggleStandort: (standortId: number, enabled: boolean) => void;
  onSaveStandorte: () => void;
  onCancelStandorteEdit: () => void;
  onStartEditStammdaten: () => void;
  onSaveStammdaten: () => void;
  onCancelEditStammdaten: () => void;
  emailCheck?: { status: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'; email: string };
  /** Direkt aus dem Datensatz anrufen bzw. schreiben (T-0153). */
  onAnrufen?: (nummer: string) => void;
  onNachricht?: (kanal: 'email' | 'sms', wert: string) => void;
};

/** Welche Wege ein Kontaktweg anbietet: Mail schreiben, SMS schreiben, anrufen. */
function kontaktAktionen(typ: string): { mail: boolean; sms: boolean; anruf: boolean } {
  return {
    mail: typ === 'email',
    sms: typ === 'mobil' || typ === 'sms',
    anruf: typ === 'mobil' || typ === 'festnetz' || typ === 'phone',
  };
}

export default function PersonOverviewTab({
  person,
  isEditing,
  isSaving,
  formData,
  getAgeText,
  handleSelectChange,
  handleInputChange,
  handleCheckboxChange,
  addressQuery,
  onAddressQueryChange,
  addressSuggestions,
  onAddressSuggestionSelect,
  onStructuredAddressFieldChange,
  onCommunicationTypeChange,
  onCommunicationValueChange,
  onCommunicationOfficeAllowedChange,
  onRemoveCommunication,
  onAddCommunication,
  canEditStandorte,
  isEditingStandorte,
  setIsEditingStandorte,
  standorte,
  personStandorteIds,
  abgeleiteteStandorteIds = [],
  onToggleStandort,
  onSaveStandorte,
  onCancelStandorteEdit,
  onStartEditStammdaten,
  onSaveStammdaten,
  onCancelEditStammdaten,
  emailCheck,
  onAnrufen,
  onNachricht,
}: Readonly<Props>) {
  const isCompany = Boolean(formData.isCompany);
  const emailHint = (() => {
    if (!emailCheck || !isEditing) return null;
    switch (emailCheck.status) {
      case 'checking': return { text: 'E-Mail wird geprüft…', cls: 'text-muted-foreground' };
      case 'available': return formData.email && formData.email !== person.email
        ? { text: 'E-Mail-Adresse ist frei', cls: 'text-green-600' }
        : null;
      case 'taken': return { text: 'E-Mail bereits vergeben — Speichern nicht möglich.', cls: 'text-red-600' };
      case 'invalid': return { text: 'E-Mail-Format ungültig.', cls: 'text-red-600' };
      default: return null;
    }
  })();
  const emailBlocksSave = emailCheck?.status === 'taken' || emailCheck?.status === 'invalid';

  const stammdatenActions = isEditing ? (
    <>
      <Button variant="outline" size="sm" onClick={onCancelEditStammdaten} disabled={isSaving}>
        Abbrechen
      </Button>
      <Button size="sm" onClick={onSaveStammdaten} disabled={isSaving || emailBlocksSave}>
        Speichern
      </Button>
    </>
  ) : (
    <Button variant="outline" size="sm" onClick={onStartEditStammdaten}>
      Bearbeiten
    </Button>
  );

  const standorteActions = canEditStandorte ? (
    !isEditingStandorte ? (
      <Button variant="outline" size="sm" onClick={() => setIsEditingStandorte(true)}>
        <Edit className="mr-1 h-3.5 w-3.5" />
        Bearbeiten
      </Button>
    ) : (
      <>
        <Button size="sm" variant="outline" onClick={onCancelStandorteEdit}>Abbrechen</Button>
        <Button size="sm" onClick={onSaveStandorte}>Speichern</Button>
      </>
    )
  ) : undefined;

  return (
    <TabsContent value="general" className="space-y-4">
      {/* Standardmäßig aufgeklappt — das spart im Alltag einen Klick (T-0051). */}
      <PersonCollapsibleSection
        title="Stammdaten"
        description="Verwalten Sie die allgemeinen Personendaten"
        defaultOpen
        actions={stammdatenActions}
      >
        <div className="space-y-4">
          {isEditing ? (
            <div className="flex items-center gap-2 rounded-md border p-3">
              <Checkbox
                id="isCompany"
                checked={isCompany}
                onCheckedChange={(checked) => handleCheckboxChange('isCompany', checked)}
              />
              <label htmlFor="isCompany" className="text-sm font-medium cursor-pointer">
                Firmenkunde
              </label>
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            {isCompany ? (
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="companyName" className="text-sm font-medium">Firmenname</label>
                {isEditing ? <div className="flex items-center">
                    <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                    <Input id="companyName" name="companyName" value={formData.companyName || ''} onChange={handleInputChange} />
                  </div> : <div className="flex items-center rounded-md border p-3">
                    <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{formData.companyName || '-'}</span>
                  </div>}
              </div>
            ) : null}
            <div className="space-y-2">
              <label htmlFor="salutation" className="text-sm font-medium">Anrede</label>
              {isEditing ? <Select value={formData.salutation ?? ''} onValueChange={(value: string) => handleSelectChange('salutation', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Anrede auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Divers">Divers</SelectItem>
                  </SelectContent>
                </Select> : <div className="rounded-md border p-3">
                  {person.salutation || '-'}
                </div>}
            </div>
            <div className="space-y-2">
              <label htmlFor="birthDate" className="text-sm font-medium">Geburtsdatum</label>
              {isEditing ? <div className="flex items-center gap-2">
                  <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                  <Input id="birthDate" name="birthDate" type="date" value={formData.birthDate || ''} onChange={handleInputChange} />
                  {formData.birthDate && <span className="text-xs text-muted-foreground">{getAgeText(formData.birthDate)}</span>}
                </div> : <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{person.birthDate ? formatDate(person.birthDate) : '-'}</span>
                  </div>
                  {person.birthDate && <span className="text-xs text-muted-foreground">{getAgeText(person.birthDate)}</span>}
                </div>}
            </div>
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium">{isCompany ? 'Ansprechpartner — Vorname' : 'Vorname'}</label>
              {isEditing ? <div className="flex items-center">
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <Input id="firstName" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} />
                </div> : <div className="flex items-center rounded-md border p-3">
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{formData.firstName || '-'}</span>
                </div>}
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium">{isCompany ? 'Ansprechpartner — Nachname' : 'Nachname'}</label>
              {isEditing ? <div className="flex items-center">
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <Input id="lastName" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} />
                </div> : <div className="flex items-center rounded-md border p-3">
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{formData.lastName || '-'}</span>
                </div>}
            </div>
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="email" className="text-sm font-medium">Primäre E-Mail (Login/Vertrag)</label>
              {isEditing ? (
                <div className="space-y-1">
                  <div className="flex items-center">
                    <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      className={emailBlocksSave ? 'border-red-500 focus-visible:ring-red-500' : undefined}
                    />
                  </div>
                  {emailHint ? <p className={`text-xs ${emailHint.cls}`}>{emailHint.text}</p> : null}
                </div>
              ) : (
                <div className="flex items-center rounded-md border p-3">
                  <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>
                    {(
                      (formData.communicationChannels || formData.communications || []).find((c: any) => c.type === 'email' && c.isPrimary)?.value
                      || (formData.communicationChannels || formData.communications || []).find((c: any) => c.type === 'email')?.value
                      || person.email
                      || '-'
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2 relative">
            <label htmlFor="address" className="text-sm font-medium">Adresse</label>
            {isEditing ? <div className="flex-1">
                <div className="flex items-start">
                  <MapPin className="mr-2 mt-2 h-4 w-4 text-muted-foreground" />
                  <Input id="address" name="address" autoComplete="off" value={addressQuery} onChange={e => onAddressQueryChange(e.target.value)} placeholder="Adresse suchen..." />
                </div>
                {addressSuggestions.length > 0 && <div className="absolute z-10 mt-2 w-full rounded-md border bg-background shadow">
                    {addressSuggestions.map(s => <button key={s.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted" onClick={() => onAddressSuggestionSelect(s)}>
                        {s.label}
                      </button>)}
                  </div>}
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <Input
                    value={String(formData.structuredAddress?.street || '')}
                    onChange={(e) => onStructuredAddressFieldChange('street', e.target.value)}
                    placeholder="Straße"
                  />
                  <Input
                    value={String(formData.structuredAddress?.zip || formData.structuredAddress?.postalCode || '')}
                    onChange={(e) => onStructuredAddressFieldChange('zip', e.target.value)}
                    placeholder="PLZ"
                  />
                  <Input
                    value={String(formData.structuredAddress?.city || '')}
                    onChange={(e) => onStructuredAddressFieldChange('city', e.target.value)}
                    placeholder="Ort"
                  />
                </div>
              </div> : <div className="flex items-center rounded-md border p-3">
                <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{person.address || formatStructuredAddress(person.structuredAddress) || '-'}</span>
              </div>}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Kommunikation</div>
              {isEditing ? (
                <Button type="button" variant="outline" size="sm" onClick={onAddCommunication}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Kontaktweg hinzufügen
                </Button>
              ) : null}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                {(formData.communicationChannels || formData.communications || []).map((c: any, idx: number) => {
                  const TypeIcon = COMM_TYPE_ICONS[(c.type ?? 'mobil') as string] ?? MessageSquare;
                  return (
                    <div key={`${c.type || 'comm'}-${idx}`} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
                      <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Select value={c.type} onValueChange={(val) => onCommunicationTypeChange(idx, val)}>
                        <SelectTrigger className="w-32 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">E-Mail</SelectItem>
                          <SelectItem value="mobil">Mobil</SelectItem>
                          <SelectItem value="festnetz">Festnetz</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        className="flex-1 min-w-[180px]"
                        placeholder={c.type === 'email' ? 'name@beispiel.de' : '+49 …'}
                        value={c.value || ''}
                        onChange={(e) => onCommunicationValueChange(idx, e.target.value)}
                      />
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap cursor-pointer select-none">
                        <Checkbox
                          checked={c.officeContactAllowed !== false}
                          onCheckedChange={(val) => onCommunicationOfficeAllowedChange(idx, val === true)}
                        />
                        <span title="Darf das Büro diesen Kontaktweg nutzen?">Büro darf kontaktieren</span>
                      </label>
                      <Button type="button" variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => onRemoveCommunication(idx)} aria-label="Entfernen">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                {((formData.communicationChannels || formData.communications || []).length === 0) ? (
                  <p className="text-xs text-muted-foreground py-2">Noch keine Kontaktwege hinterlegt.</p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-1.5">
                {(person.communicationChannels && person.communicationChannels.length > 0 ? person.communicationChannels : person.communications)?.length ? (
                  (person.communicationChannels && person.communicationChannels.length > 0 ? person.communicationChannels : person.communications)?.map((c: any, idx: number) => {
                    const TypeIcon = COMM_TYPE_ICONS[(c.type ?? 'mobil') as string] ?? MessageSquare;
                    const typeLabel = COMM_TYPE_LABELS[(c.type ?? 'mobil') as string] ?? c.type;
                    return (
                      <div key={`${c.type || 'comm'}-${idx}`} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                        <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-xs uppercase tracking-wide text-muted-foreground w-16 shrink-0">{typeLabel}</span>
                        <span className="flex-1 break-all">{c.value || '—'}</span>
                        {c.isPrimary ? (
                          <span className="rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">Primär</span>
                        ) : null}
                        {c.officeContactAllowed === false ? (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">Office gesperrt</span>
                        ) : null}
                        {c.vomKundenGesperrt ? (
                          // Im Kundenbereich gesperrt — vorher hier nicht zu sehen (T-0207).
                          <span
                            className="text-xs text-muted-foreground whitespace-nowrap"
                            title="Der Kunde hat diesen Kontaktweg im Kundenbereich fürs Büro gesperrt"
                          >
                            vom Kunden gesperrt
                          </span>
                        ) : null}
                        {c.value && c.officeContactAllowed !== false && !c.vomKundenGesperrt ? (() => {
                          const aktionen = kontaktAktionen(String(c.type ?? 'mobil'));
                          return (
                            <span className="flex shrink-0 items-center gap-1">
                              {aktionen.mail && onNachricht ? (
                                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" title="E-Mail schreiben" aria-label="E-Mail schreiben" onClick={() => onNachricht('email', c.value)}>
                                  <Mail className="h-4 w-4" />
                                </Button>
                              ) : null}
                              {aktionen.sms && onNachricht ? (
                                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" title="SMS schreiben" aria-label="SMS schreiben" onClick={() => onNachricht('sms', c.value)}>
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              ) : null}
                              {aktionen.anruf && onAnrufen ? (
                                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" title="Anrufen" aria-label="Anrufen" onClick={() => onAnrufen(c.value)}>
                                  <Phone className="h-4 w-4" />
                                </Button>
                              ) : null}
                            </span>
                          );
                        })() : null}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground py-2">Keine Kontaktwege hinterlegt.</p>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="notes" className="text-sm font-medium">Notizen</label>
            {isEditing ? <div className="flex items-start">
                <FileText className="mr-2 mt-2 h-4 w-4 text-muted-foreground" />
                <Textarea id="notes" name="notes" rows={4} value={formData.notes || ''} onChange={handleInputChange} />
              </div> : <div className="flex items-start rounded-md border p-3">
                <FileText className="mr-2 mt-0.5 h-4 w-4 text-muted-foreground" />
                <span>{person.notes || '-'}</span>
              </div>}
          </div>
        </div>
      </PersonCollapsibleSection>

      {canEditStandorte ? (
        <PersonCollapsibleSection
          title="Standorte"
          description="Standortzuordnungen dieser Person"
          actions={standorteActions}
        >
          {isEditingStandorte ? (
            <div className="rounded-md border p-3 space-y-2">
              <div className="text-xs text-muted-foreground">
                Wählen Sie die Standorte aus, denen diese Person zugeordnet werden soll.
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {standorte.map((s) => {
                  const checked = personStandorteIds.includes(s.id);
                  return (
                    <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => onToggleStandort(s.id, Boolean(v))}
                      />
                      <span>{s.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-md border p-3">
              {personStandorteIds.length > 0 || abgeleiteteStandorteIds.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {personStandorteIds.map((sid) => {
                    const standort = standorte.find((s) => s.id === sid);
                    return standort ? (
                      <Badge key={sid} variant="secondary">{standort.name}</Badge>
                    ) : null;
                  })}
                  {/* Aus Probestunden und Kursen (T-0260/T-0276): Ohne sie sah eine Person
                      mit Kursen in Oldenburg aus, als gehöre sie nur zur Testschule. */}
                  {abgeleiteteStandorteIds.map((sid) => {
                    const standort = standorte.find((s) => s.id === sid);
                    return standort ? (
                      <Badge key={`abgeleitet-${sid}`} variant="outline" title="Aus Probestunden und Kursen, nicht von Hand zugeordnet">
                        {standort.name} · aus Probestunden/Kursen
                      </Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Keine Standorte zugeordnet</span>
              )}
            </div>
          )}
        </PersonCollapsibleSection>
      ) : null}
    </TabsContent>
  );
}
